import React, { memo } from 'react'
import { List, Space, Button, Tooltip, Badge, Tag } from 'antd'
import {
  CheckOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  EditOutlined,
  WarningOutlined,
  FlagOutlined
} from '@ant-design/icons'
import { TASK_TYPE_CONFIG } from '../config/taskTypes'
import { STATUS_CONFIG, STATUS } from '../config/taskStatus'
import { getPriorityColor, getDisplayName, capitalizeFirstLetter } from '../utils/taskUtils'
import styles from '../styles/TaskItem.module.css'
import dayjs from 'dayjs'

const TaskItem = ({ task, isOverdue, onComplete, onDelete, onEdit, canDelete }) => {
  const getTypeTag = () => {
    const config = TASK_TYPE_CONFIG[task.taskType]
    return (
      <Tag icon={config?.icon} color={config?.color} className={styles.typeTag}>
        {config?.label}
      </Tag>
    )
  }

  const getStatusTag = () => {
    const config = STATUS_CONFIG[task.status]
    return (
      <Tag color={config?.color} className={styles.statusTag}>
        {config?.text}
      </Tag>
    )
  }

  const getPriorityTag = () => {
    if (!task.priority) return null

    const priorityColors = {
      high: '#f5222d',
      medium: '#faad14',
      low: '#52c41a'
    }

    return (
      <Tag icon={<FlagOutlined />} color={priorityColors[task.priority.toLowerCase()]} className={styles.priorityTag}>
        {capitalizeFirstLetter(task.priority)}
      </Tag>
    )
  }

  return (
    <List.Item className={`${styles.taskItem} ${styles[task.status]} ${isOverdue ? styles.overdue : ''}`}>
      <div className={styles.taskHeaderRight}>{getPriorityTag()}</div>
      <div className={styles.taskContent}>
        <div className={styles.taskMain}>
          <div className={styles.taskTop}>
            <div className={styles.taskDescription}>
              <Badge
                color={task.priority ? getPriorityColor(task.priority) : '#1890ff'}
                text={task.description}
                className={styles.taskText}
              />
            </div>
          </div>
          <div className={styles.taskHeader}>
            <div className={styles.taskHeaderLeft}>
              {getTypeTag()}
              {getStatusTag()}
            </div>
          </div>
          <div className={styles.taskMeta}>
            <div className={styles.taskMetaGroup}>
              <span className={`${styles.taskDate} ${isOverdue ? styles.overdueDate : ''}`}>
                <ClockCircleOutlined /> Due: {dayjs(task.dueDate).format('DD-MM-YYYY')}
              </span>
              {isOverdue && (
                <Tag color='red' icon={<WarningOutlined />} className={styles.overdueTag}>
                  OVERDUE
                </Tag>
              )}
            </div>
            <div className={styles.taskMetaGroup}>
              <span className={styles.taskCreator}>
                <UserOutlined /> Created by:{' '}
                {getDisplayName(task.assignedBy?.firstName + ' ' + task.assignedBy?.lastName)}
              </span>
              <span className={styles.taskResponsible}>
                Assigned to: {getDisplayName(task.assignedTo?.firstName + ' ' + task.assignedTo?.lastName)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.taskRightSide}>
        <Space className={styles.taskActions} align='center' size='middle'>
          {task.status !== STATUS.COMPLETED && (
            <Tooltip title='Mark as Done'>
              <Button
                type='primary'
                icon={
                  isOverdue ? (
                    <CheckOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                  ) : (
                    <CheckOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                  )
                }
                className={`${styles.completeButton} ${isOverdue ? styles.overdueAction : ''}`}
                onClick={() => onComplete(task._id)}
                style={{ marginRight: '16px' }}
              />
            </Tooltip>
          )}
          <Tooltip title='Edit Task'>
            <Button type='default' icon={<EditOutlined />} className={styles.editButton} onClick={() => onEdit(task)} />
          </Tooltip>
          {canDelete && (
            <Tooltip title='Delete Task'>
              <Button type='text' danger icon={<DeleteOutlined />} onClick={() => onDelete(task._id)} />
            </Tooltip>
          )}
        </Space>
      </div>
    </List.Item>
  )
}

export default memo(TaskItem)
